import type { IntlInvoiceRefresh } from '@staff-app/shared-types';

/**
 * What the one Refresh button says afterwards, from two independent answers.
 *
 * ── PURE, AND EXTRACTED ON PURPOSE ────────────────────────────────────────────────────────────
 *
 * The page has no test harness (this feature unit-tests components, not pages), and the branching
 * here is the part with real judgement in it: four outcomes per half, times two halves, times the
 * question of what to say when one half fails and the other did not. Left inline it would be the
 * only untested logic in the lane. Out here it is a function that takes two settled results and
 * returns a string.
 *
 * COUNTS, SAID OUT LOUD. "Checked 4, nothing moved" and "nothing to check" are the same silent
 * non-event without them, and the difference is whether anyone should be worried — the same
 * reasoning the Stripe half already carried before Amazon joined it.
 *
 * EACH HALF NAMES ITS OWN SYSTEM when it fails. "Could not reach Stripe" and "could not reach
 * Amazon" send somebody to different places, and a merged "refresh failed" would send them to
 * neither.
 */
export type RefreshHalf =
  | { status: 'fulfilled'; value: IntlInvoiceRefresh | undefined }
  | { status: 'rejected'; reason: unknown };

/** One half's sentence, or null when there is genuinely nothing to say about it. */
export function refreshHalfSummary(label: string, half: RefreshHalf): string | null {
  if (half.status === 'rejected') {
    const message = (half.reason as Error)?.message ?? 'unknown error';
    return `could not reach ${label}: ${message}`;
  }
  const r = half.value;
  // No payload is not a failure and not a result — the mutation answered with nothing to report,
  // which is a shape Apollo can produce and which must not print as "checked undefined".
  if (!r) return null;
  if (r.checked === 0) return `nothing live at ${label}`;
  return (
    `${label}: checked ${r.checked}, ${r.changed} changed` +
    (r.failed ? `, ${r.failed} could not be read` : '')
  );
}

/**
 * Both halves, merged into the line under the button.
 *
 * Returns null when neither half had anything to say, so the caller leaves the previous message
 * alone rather than replacing it with an empty string — a banner that blanks itself reads as the
 * button having done nothing.
 */
export function refreshSummary(stripe: RefreshHalf, amazon: RefreshHalf): string | null {
  const parts = [
    refreshHalfSummary('Stripe', stripe),
    refreshHalfSummary('Amazon', amazon),
  ].filter((p): p is string => Boolean(p));
  return parts.length ? `${parts.join('. ')}.` : null;
}
